<script lang="ts">
  import { uploadFile, uploadMedia, computeSha256, type VideoMode } from '../lib/blossom';
  import { calculatePriceSats, calculateMediaPriceSats } from '../lib/pricing';
  import { SERVER } from '../lib/stores';

  type UploadState = 'idle' | 'selected' | 'hashing' | 'paying' | 'uploading' | 'success' | 'error';

  let isDragging = $state(false);
  let state = $state<UploadState>('idle');
  let selectedFile = $state<File | null>(null);
  let fileHash = $state<string | null>(null);
  let uploadedUrl = $state<string | null>(null);
  let uploadedExpiry = $state<number | null>(null);
  let errorMsg = $state<string | null>(null);
  let copyFeedback = $state(false);
  let uploadProgress = $state(0);
  let paymentStatus = $state('');
  let walletBalance = $state<number | null>(null);
  let optimizeMedia = $state(false);
  let videoMode = $state<VideoMode>('thumbnail');

  let fileInput: HTMLInputElement | undefined = $state();

  const MAX_FILE_SIZE = 100 * 1024 * 1024;
  const FREE_TIER_LIMIT = 1024 * 1024;

  let isImage = $derived(selectedFile !== null && selectedFile.type.startsWith('image/'));
  let isVideo = $derived(selectedFile !== null && selectedFile.type.startsWith('video/'));
  let isMediaFile = $derived(isImage || isVideo);
  let mediaPriceSats = $derived(
    optimizeMedia && selectedFile
      ? calculateMediaPriceSats({ type: selectedFile.type, size: selectedFile.size }, videoMode)
      : 0,
  );
  let storagePriceSats = $derived(
    selectedFile !== null && selectedFile.size >= FREE_TIER_LIMIT
      ? calculatePriceSats(selectedFile.size)
      : 0,
  );
  let totalPriceSats = $derived(storagePriceSats + mediaPriceSats);
  let requiresPayment = $derived(totalPriceSats > 0);
  let canPayFromWallet = $derived(walletBalance !== null && walletBalance >= totalPriceSats);

  function formatFileSize(bytes: number): string {
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(2) + ' GB';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(2) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return bytes + ' B';
  }

  function formatDate(ts: number): string {
    return new Date(ts).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function truncateHash(hash: string): string {
    return hash.slice(0, 12) + '...' + hash.slice(-8);
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    isDragging = true;
  }

  function handleDragLeave(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    isDragging = false;
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    isDragging = false;
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    selectFile(files[0]);
  }

  function handleClick() {
    fileInput?.click();
  }

  function handleFileChange() {
    if (!fileInput?.files?.length) return;
    selectFile(fileInput.files[0]);
    fileInput.value = '';
  }

  function selectFile(file: File) {
    if (file.size > MAX_FILE_SIZE) {
      state = 'error';
      errorMsg = `File exceeds 100MB limit (${formatFileSize(file.size)})`;
      return;
    }
    if (file.size === 0) {
      state = 'error';
      errorMsg = 'Cannot upload an empty file';
      return;
    }
    selectedFile = file;
    state = 'selected';
    fileHash = null;
    uploadedUrl = null;
    errorMsg = null;
    optimizeMedia = false;
    videoMode = 'thumbnail';
    if (requiresPayment) {
      import('../lib/wallet').then(({ getBalance }) => getBalance()).then(b => { walletBalance = b; }).catch(() => {});
    }
  }

  async function handleUpload() {
    if (!selectedFile) return;

    try {
      state = 'hashing';
      fileHash = await computeSha256(selectedFile);

      let cashuToken: string | undefined;
      if (requiresPayment) {
        state = 'paying';
        paymentStatus = canPayFromWallet ? `Paying ${totalPriceSats} sats from wallet...` : 'Connecting to mint...';
        const { payForUpload } = await import('../lib/cashu-payment');
        cashuToken = await payForUpload(totalPriceSats, (s) => { paymentStatus = s; });
      }

      state = 'uploading';
      uploadProgress = 0;

      let result: { url?: string; sha256?: string };
      if (optimizeMedia && isMediaFile && cashuToken) {
        result = await uploadMedia(selectedFile, cashuToken, videoMode, (p) => { uploadProgress = p; }) as { url?: string; sha256?: string };
      } else {
        result = await uploadFile(selectedFile, cashuToken, (p) => { uploadProgress = p; }) as { url?: string; sha256?: string };
      }

      uploadedUrl = result?.url ?? `${SERVER}/${fileHash}`;
      uploadedExpiry = (result as any)?.expires_at ?? null;
      state = 'success';
    } catch (err) {
      state = 'error';
      errorMsg = err instanceof Error ? err.message : 'Upload failed';
    }
  }

  function handleRetry() {
    state = selectedFile ? 'selected' : 'idle';
    errorMsg = null;
    uploadedUrl = null;
    fileHash = null;
    paymentStatus = '';
    uploadProgress = 0;
  }

    function handleReset() {
    state = 'idle';
    selectedFile = null;
    fileHash = null;
    uploadedUrl = null;
    errorMsg = null;
    uploadProgress = 0;
  }

  async function copyUrl() {
    if (!uploadedUrl) return;
    await navigator.clipboard.writeText(uploadedUrl);
    copyFeedback = true;
    setTimeout(() => { copyFeedback = false; }, 2000);
  }
</script>

<div class="space-y-4">
  <!-- Drop Zone -->
  <div
    class="relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 {isDragging
      ? 'border-[#58a6ff] bg-[#58a6ff]/5'
      : state === 'error'
        ? 'border-[#f85149]/50 bg-[#f85149]/5'
        : 'border-[#21262d] bg-[#161b22] hover:border-[#30363d] hover:bg-[#1c2129]'
    }"
    role="button"
    tabindex="0"
    onclick={handleClick}
    onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
    ondragover={handleDragOver}
    ondragleave={handleDragLeave}
    ondrop={handleDrop}
  >
    <input
      bind:this={fileInput}
      type="file"
      class="hidden"
      onchange={handleFileChange}
    />

    {#if state === 'idle' || state === 'error'}
      <!-- Upload Icon -->
      <div class="flex flex-col items-center gap-3 pointer-events-none">
        <svg class="w-10 h-10 text-[#484f58]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
        </svg>
        <p class="text-[#e6edf3] text-sm font-medium">
          Drop a file here, or click to browse
        </p>
        <p class="text-[#484f58] text-xs">
          Maximum file size: 100MB
        </p>
      </div>
    {:else if state === 'uploading'}
      <div class="flex flex-col items-center gap-3 pointer-events-none w-full max-w-xs mx-auto">
        <div class="w-full bg-[#21262d] rounded-full h-2 overflow-hidden">
          <div class="h-full bg-[#58a6ff] rounded-full transition-all duration-300" style="width: {uploadProgress}%"></div>
        </div>
        <p class="text-[#e6edf3] text-sm font-medium">
          Uploading... {uploadProgress}%
        </p>
      </div>
    {:else if state === 'hashing' || state === 'paying'}
      <div class="flex flex-col items-center gap-3 pointer-events-none">
        <div class="relative w-10 h-10">
          <div class="absolute inset-0 rounded-full border-2 border-[#21262d]"></div>
          <div class="absolute inset-0 rounded-full border-2 border-t-[#58a6ff] animate-spin"></div>
        </div>
        <p class="text-[#e6edf3] text-sm font-medium">
          {state === 'hashing' ? 'Computing file hash...' : state === 'paying' ? paymentStatus : state === 'uploading' ? (requiresPayment ? 'Verifying payment & uploading...' : 'Uploading...') : 'Processing...'}
        </p>
      </div>
    {:else if state === 'success'}
      <div class="flex flex-col items-center gap-3 pointer-events-none">
        <svg class="w-10 h-10 text-[#3fb950]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-[#3fb950] text-sm font-medium">Upload complete</p>
      </div>
    {/if}
  </div>

  <!-- Free Tier Notice -->
  {#if state === 'idle'}
    <div class="flex items-start gap-2 px-3 py-2 rounded-md bg-[#161b22] border border-[#21262d]">
      <svg class="w-4 h-4 text-[#8b949e] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
      </svg>
      <p class="text-xs text-[#8b949e]">
        Files under 1MB are stored free for 30 days. Larger files require Cashu payment.
      </p>
    </div>
  {/if}

  <!-- File Preview -->
  {#if selectedFile && (state === 'selected' || state === 'hashing' || state === 'paying' || state === 'uploading' || state === 'success' || state === 'error')}
    <div class="bg-[#161b22] border border-[#21262d] rounded-lg overflow-hidden">
      <!-- File Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-[#21262d]">
        <div class="flex items-center gap-3 min-w-0">
          <svg class="w-5 h-5 text-[#8b949e] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <span class="text-sm text-[#e6edf3] font-medium truncate">{selectedFile.name}</span>
        </div>
        {#if state === 'selected' || state === 'error'}
          <button
            class="text-[#8b949e] hover:text-[#e6edf3] transition-colors p-1"
            onclick={(e) => { e.stopPropagation(); handleReset(); }}
            title="Remove file"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        {/if}
      </div>

      <!-- File Details -->
      <div class="px-4 py-3 space-y-2">
        <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div>
            <span class="text-[#8b949e]">Size</span>
            <p class="text-[#e6edf3] font-mono text-xs mt-0.5">{formatFileSize(selectedFile.size)}</p>
          </div>
          <div>
            <span class="text-[#8b949e]">Type</span>
            <p class="text-[#e6edf3] text-xs mt-0.5">{selectedFile.type || 'application/octet-stream'}</p>
          </div>
          <div>
            <span class="text-[#8b949e]">Modified</span>
            <p class="text-[#e6edf3] text-xs mt-0.5">{formatDate(selectedFile.lastModified)}</p>
          </div>
          {#if fileHash}
            <div class="col-span-2 sm:col-span-1">
              <span class="text-[#8b949e]">SHA-256</span>
              <p class="text-[#e6edf3] font-mono text-xs mt-0.5 break-all" title={fileHash}>
                {truncateHash(fileHash)}
              </p>
            </div>
          {/if}
        </div>

        <!-- Size tier notice -->
        {#if requiresPayment && state === 'selected'}
          <div class="flex items-center gap-2 px-3 py-2 rounded-md bg-[#1c1525] border border-[#341f6e]">
            <svg class="w-4 h-4 text-[#a371f7] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
            </svg>
            <div class="flex-1">
              <p class="text-xs text-[#a371f7]">
                {totalPriceSats} sats{canPayFromWallet
                  ? ` — paid from wallet (${walletBalance} sats)`
                  : walletBalance !== null
                    ? ` — wallet has ${walletBalance}, will mint ${totalPriceSats - (walletBalance || 0)} new`
                    : ' — paid via testnut Cashu mint'}
              </p>
              {#if optimizeMedia && mediaPriceSats > 0}
                <p class="text-[10px] text-[#8b949e] mt-0.5">
                  {storagePriceSats > 0 ? `${storagePriceSats} storage + ` : ''}{mediaPriceSats} {isImage ? 'image optimization' : videoMode === 'thumbnail' ? 'video thumbnail' : 'video transcode'}
                </p>
              {/if}
            </div>
          </div>
        {/if}

        <!-- Media Optimization Toggle -->
        {#if isMediaFile && state === 'selected'}
          <div class="flex items-start gap-2 px-3 py-2 rounded-md bg-[#161b22] border border-[#21262d]">
            <button
              type="button"
              role="switch"
              aria-checked={optimizeMedia}
              class="mt-0.5 shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors {optimizeMedia ? 'bg-[#238636]' : 'bg-[#30363d]'}"
              onclick={() => optimizeMedia = !optimizeMedia}
            >
              <span class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform {optimizeMedia ? 'translate-x-4' : 'translate-x-1'}" />
            </button>
            <div class="flex-1 min-w-0">
              <p class="text-xs text-[#e6edf3] font-medium">
                {isImage ? 'Optimize image' : 'Optimize video'}
              </p>
              <p class="text-[10px] text-[#8b949e]">
                {isImage
                  ? 'Resize to 1920px & convert to WebP — 21 sats'
                  : videoMode === 'thumbnail'
                    ? 'Generate thumbnail — 21 sats'
                    : `Transcode to 720p MP4 + thumbnail — ${mediaPriceSats} sats`}
              </p>
              {#if isVideo && optimizeMedia}
                <div class="flex gap-2 mt-1.5">
                  <button
                    type="button"
                    class="text-[10px] px-2 py-0.5 rounded {videoMode === 'thumbnail' ? 'bg-[#238636] text-white' : 'bg-[#21262d] text-[#8b949e] hover:text-[#e6edf3]'} transition-colors"
                    onclick={() => videoMode = 'thumbnail'}
                  >
                    Thumbnail (21 sats)
                  </button>
                  <button
                    type="button"
                    class="text-[10px] px-2 py-0.5 rounded {videoMode === 'transcode' ? 'bg-[#238636] text-white' : 'bg-[#21262d] text-[#8b949e] hover:text-[#e6edf3]'} transition-colors"
                    onclick={() => videoMode = 'transcode'}
                  >
                    Full transcode ({Math.max(21, Math.ceil((selectedFile?.size ?? 0) / 1_000_000) * 21)} sats)
                  </button>
                </div>
              {/if}
            </div>
          </div>
        {/if}
      </div>

      <!-- Actions -->
      {#if state === 'selected'}
        <div class="px-4 py-3 border-t border-[#21262d] bg-[#0d1117]/50">
          <button
            class="w-full px-4 py-2.5 bg-[#238636] hover:bg-[#2ea043] text-white rounded-md text-sm font-medium transition-colors"
            onclick={handleUpload}
          >
            {requiresPayment ? (canPayFromWallet ? `Pay ${totalPriceSats} sats & Upload` : `Mint ${totalPriceSats} sats & Upload`) : 'Upload'}
          </button>
        </div>
      {:else if state === 'success' && uploadedUrl}
        <div class="px-4 py-3 border-t border-[#21262d] bg-[#0d1117]/50 space-y-2">
          <div class="flex items-center gap-2">
            <code class="flex-1 text-xs font-mono text-[#3fb950] bg-[#0d1117] px-3 py-2 rounded-md border border-[#21262d] break-all select-all">
              {uploadedUrl}
            </code>
            <button
              class="shrink-0 px-3 py-2 text-xs font-medium rounded-md border border-[#21262d] bg-[#21262d] hover:bg-[#30363d] text-[#e6edf3] transition-colors"
              onclick={copyUrl}
            >
              {copyFeedback ? 'Copied' : 'Copy'}
            </button>
          </div>
          {#if uploadedExpiry}
            <div class="flex items-center gap-3 text-xs">
              <span class="text-[#8b949e]">📅 Expires {new Date(uploadedExpiry * 1000).toLocaleDateString()}</span>
              <span class="text-[#484f58]">({Math.ceil((uploadedExpiry - Date.now() / 1000) / 86400)} days)</span>
            </div>
          {/if}
          <button
            class="w-full px-4 py-2 text-[#58a6ff] hover:text-[#79c0ff] text-sm font-medium transition-colors"
            onclick={handleReset}
          >
            Upload another file
          </button>
        </div>
      {:else if state === 'error'}
        <div class="px-4 py-3 border-t border-[#21262d] bg-[#0d1117]/50 space-y-2">
          {#if errorMsg}
            <p class="text-sm text-[#f85149]">{errorMsg}</p>
          {/if}
          <button
            class="px-4 py-2 bg-[#238636] hover:bg-[#2ea043] text-white rounded-md text-sm font-medium transition-colors"
            onclick={handleRetry}
          >
            Retry
          </button>
        </div>
      {/if}
    </div>
  {/if}
</div>
